import React, { useRef, useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { FormSpy } from 'react-final-form';
import { TextInput, ReferenceArrayInput } from 'react-admin';
import { Grid, makeStyles } from '@material-ui/core';
import FormHelperText from '@material-ui/core/FormHelperText';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import GridInput from '../../../core/components/inputs/GridInput';
import CheckBoxInput from '../../../core/components/inputs/CheckBoxInput';
import { isValidMultipleDomains } from '../../../core/utils/index';

const useStyles = makeStyles({
  checkboxLabel: {
    color: '#757575'
  },
  checkbox: {
    display: 'flex',
    marginRight: '12px',
    '& label': {
      color: '#212121',
      display: 'flex',
      alignItems: 'center'
    }
  },
  firstCheckbox: {
    display: 'flex',
    marginRight: '12px',
    '& label': {
      color: '#212121',
      display: 'flex',
      alignItems: 'center'
    },
    marginLeft: '-7px'
  },
  leftWrapper: {
    flexGrow: 1
  },
  form: {
    '& > div': {
      padding: '0px !important'
    }
  }
});

/**
 * Form level validation for Option
 * @param {*} values Option values
 * @returns errors object
 */
export const validateOption = values => {
  const errors = {};
  if (!values.includeUsersWhoHaveNotLoggedIn && !values.includeLoggedInUsers) {
    errors.includeUsersWhoHaveNotLoggedIn =
      'At least one of: Not Logged-In Contacts, Logged-In Contacts must be checked';
  }
  return errors;
};

/**
 * Validate domains value
 * @param {*} value
 */
const validateDomains = value => {
  if (value && value.length > 0 && !isValidMultipleDomains(value)) {
    return 'Please enter a valid domain or multiple domain separated by comma';
  }
  return undefined;
};

/**
 * Parse a comma separated list of domains as an array
 * @param {*} value comma separated list
 * @returns array of domains
 */
const domainsParser = value => {
  if (value && value.length > 0) {
    return value.split(',');
  }
  return [];
};

const AdvancedOptionsDialog = props => {
  const classes = useStyles();
  const {
    handleClose,
    showDialog,
    option = {},
    handleSetAdvancedOptions
  } = props;

  const handleApplyClick = advancedFormProps => {
    // Trigger a submit of our custom form
    advancedFormProps.form.submit();
  };

  const formRef = useRef();
  const [contactsCheckBoxDisabled, setContactsCheckBoxDisabled] = useState(
    true
  );

  useEffect(() => {
    if (!formRef.current) return;
    if (
      contactsCheckBoxDisabled &&
      formRef.current.getState().values.includeUsersWhoDidNotAccessDocument
    ) {
      formRef.current.change('includeUsersWhoDidNotAccessDocument', false);
    }
  }, [contactsCheckBoxDisabled]);

  const handleFormChange = values => {
    if (!values || !formRef.current) return;
    if (
      formRef.current.getState().values.resourceUploadIds === undefined ||
      formRef.current.getState().values.resourceUploadIds.length === 0
    ) {
      if (!contactsCheckBoxDisabled) {
        setContactsCheckBoxDisabled(true);
      }
    } else if (
      formRef.current.getState().values.resourceUploadIds &&
      formRef.current.getState().values.resourceUploadIds.length > 0
    ) {
      if (contactsCheckBoxDisabled) {
        setContactsCheckBoxDisabled(false);
      }
    }
  };
  const handleSubmit = values => {
    handleSetAdvancedOptions(values);
    handleClose();
  };

  return (
    <Dialog
      fullWidth
      open={showDialog}
      aria-label={`Advanced Options for Summary Fund`}
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <SimpleFormWithSave
        toolbar={null}
        resource="distributionlists"
        onSave={handleSubmit}
        variant="standard"
        margin="normal"
        className={classes.form}
        initialValues={{ ...option }}
        validate={validateOption}
      >
        <FormSpy
          subscription={{ values: true }}
          onChange={formProps => handleFormChange(formProps)}
        ></FormSpy>
        <FormSpy subscription={{ pristine: true }}>
          {advancedFormProps => {
            formRef.current = advancedFormProps.form;
            const formState = advancedFormProps.form.getState() || {};
            handleFormChange(advancedFormProps);
            return (
              <>
                <DialogTitleWithCloseButton onClose={handleClose}>
                  Advanced Options for {option.fundId} Fund
                </DialogTitleWithCloseButton>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid
                        container
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Grid item>
                          <CheckBoxInput
                            name="includeAdministrator"
                            source="includeAdministrator"
                            label="Administrator"
                            className={classes.firstCheckbox}
                            helperText={false}
                            onChange={event => {
                              return event;
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <CheckBoxInput
                            name="includeUsersWhoHaveNotLoggedIn"
                            source="includeUsersWhoHaveNotLoggedIn"
                            label="Not Logged-In Contacts"
                            className={classes.checkbox}
                            helperText={false}
                            onChange={event => {
                              return event;
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <CheckBoxInput
                            name="includeLoggedInUsers"
                            source="includeLoggedInUsers"
                            label="Logged-In Contacts"
                            className={classes.checkbox}
                            helperText={false}
                            onChange={event => {
                              return event;
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <CheckBoxInput
                            name="includeInactiveInvestors"
                            source="includeInactiveInvestors"
                            label="Inactive Investors"
                            className={classes.checkbox}
                            helperText={false}
                            onChange={event => {
                              return event;
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <CheckBoxInput
                            name="includeUsersWhoDidNotAccessDocument"
                            source="includeUsersWhoDidNotAccessDocument"
                            label="Contacts Who Did Not Read Document"
                            className={classes.checkbox}
                            helperText={false}
                            disabled={contactsCheckBoxDisabled}
                            onChange={event => {
                              return event;
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          {formState &&
                            formState.errors.includeUsersWhoHaveNotLoggedIn && (
                              <FormHelperText error>
                                {
                                  formState.errors
                                    .includeUsersWhoHaveNotLoggedIn
                                }
                              </FormHelperText>
                            )}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <ReferenceArrayInput
                        label="Select By Permission Groups"
                        optionTextLabel="Permission Groups"
                        optionValueLabel="Group ID"
                        resource="groups"
                        reference="groups"
                        source="groupIds"
                        perPage={-1}
                        filter={{
                          adminGroup: false,
                          status: 'ACTIVE'
                        }}
                      >
                        <GridInput height={400} />
                      </ReferenceArrayInput>
                    </Grid>
                    <Grid item xs={6}>
                      <ReferenceArrayInput
                        label="Select By Uploaded Document"
                        optionTextLabel="Batch Name"
                        optionValueLabel="Batch ID"
                        resource="resourceuploads"
                        reference="resourceuploads"
                        source="resourceUploadIds"
                        perPage={-1}
                        filter={{
                          fundId: option.fundId,
                          batchStatus: 'ACTIVE'
                        }}
                      >
                        <GridInput
                          optionValue="resourceUploadId"
                          optionText="documentTitle"
                          height={400}
                          additionalColumns={[
                            {
                              name: 'derivedInfo.derivedDocumentTitle',
                              label: 'Document Title'
                            }
                          ]}
                          disableSelectAll={true}
                        />
                      </ReferenceArrayInput>
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        source="excludeEmailDomains"
                        label="Exclude Emails With Domains"
                        placeholder="Example: aol.com,yahoo.com"
                        options={{ autoComplete: 'off' }}
                        variant="standard"
                        helperText={false}
                        parse={domainsParser}
                        validate={validateDomains}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <div className={classes.leftWrapper}>
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      color="primary"
                    >
                      Cancel
                    </Button>
                  </div>
                  <CheckBoxInput
                    name="applyToAll"
                    source="applyToAll"
                    label="Apply To All Funds"
                    className={classes.checkbox}
                    helperText={false}
                    onChange={event => {
                      return event;
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleApplyClick(advancedFormProps)}
                  >
                    Apply
                  </Button>
                </DialogActions>
              </>
            );
          }}
        </FormSpy>
      </SimpleFormWithSave>
    </Dialog>
  );
};

export default AdvancedOptionsDialog;
